import { useLayoutEffect, useState } from "react";
import { apiMutate } from "../hooks/useApi";
import { datumDe, gueltigBisProblem, heuteBerlin, VERANSTALTUNG_VERGANGEN_SATZ, veranstaltungVergangen, vorschlagGueltigBis } from "../lib/gueltigkeit";
import { Button, DateInput, Icon, Modal } from "./components";

/**
 * „Gültigkeit verlängern“ (Antoine, 04.11.2026, Regel keine-sackgassen): der Ausweg aus „Angebot seit … abgelaufen“.
 * Setzt nur „Gültig bis“ des Vorgangs (`POST /api/events/:id/gueltigkeit-verlaengern`) — die Gastseite liest dasselbe Feld
 * live, der Gast kann danach wieder annehmen. Keine Mail, kein Neuversand, kein Geld.
 */
export function GueltigkeitVerlaengernDialog({
  open,
  onClose,
  eventId,
  gueltigBis,
  eventDate,
  onVerlaengert,
  onTerminAendern,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  gueltigBis: string | null | undefined;
  eventDate: string | null | undefined;
  onVerlaengert: () => void;
  /** Ausweg bei vergangenem Veranstaltungstag: führt zur Termin-Bearbeitung. */
  onTerminAendern?: () => void;
}) {
  const [bis, setBis] = useState(() => vorschlagGueltigBis(eventDate));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (open) {
      setBis(vorschlagGueltigBis(eventDate));
      setBusy(false);
      setFehler(null);
    }
  }, [open, eventDate]);

  const vergangen = veranstaltungVergangen(eventDate);
  const problem = vergangen ? VERANSTALTUNG_VERGANGEN_SATZ : gueltigBisProblem(bis, eventDate, heuteBerlin(), gueltigBis);

  async function speichern() {
    if (!eventId || problem) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/gueltigkeit-verlaengern`, "POST", { bis });
      onVerlaengert();
      onClose();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="460px"
      title="Gültigkeit verlängern"
      subtitle={gueltigBis ? `Bisher gültig bis ${datumDe(gueltigBis)}` : undefined}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          {vergangen && onTerminAendern ? (
            <Button type="button" data-testid="gueltigkeit-termin-aendern" onClick={() => { onClose(); onTerminAendern(); }}>
              <Icon name="edit_calendar" className="text-[16px]" /> Termin ändern
            </Button>
          ) : (
            <Button type="button" data-testid="gueltigkeit-speichern" onClick={() => void speichern()} disabled={busy || problem !== null}>
              <Icon name="event_repeat" className="text-[16px]" /> {busy ? "Speichert …" : "Gültigkeit verlängern"}
            </Button>
          )}
        </>
      }
    >
      {vergangen ? (
        <p className="text-[13px] text-on-background dark:text-inverse-on-surface" data-testid="gueltigkeit-vergangen-hinweis">{VERANSTALTUNG_VERGANGEN_SATZ}</p>
      ) : (
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Neu gültig bis</span>
          <DateInput data-testid="gueltigkeit-datum" value={bis} onValueChange={setBis} min={heuteBerlin()} max={eventDate ?? undefined} />
        </label>
        <p className="text-[12px] text-secondary-fixed-dim">
          Der Gast kann das Angebot danach wieder annehmen. Es geht keine E-Mail raus; Preise und Positionen bleiben, wie sie sind.
        </p>
        {problem && bis && !vergangen ? <p className="text-[12px] text-error dark:text-[#e39b92]" data-testid="gueltigkeit-problem">{problem}</p> : null}
        {fehler ? <p className="text-[12px] text-error dark:text-[#e39b92]" role="alert">{fehler}</p> : null}
      </div>
      )}
    </Modal>
  );
}
